import { observer } from "mobx-react-lite";
import { GroupedList, Pivot, PivotItem } from "office-ui-fabric-react";
import { useState } from "react";

import {
  Heading,
  IconButton,
  IGroupHeaderProps,
  Spinner,
  Stack,
  TooltipHost
} from "@bps/fluent-ui";
import {
  ClinicalDataType,
  ClinicalDataTypeLabel
} from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import { ClinicalRecord } from "@stores/clinical/models/ClinicalRecord.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import {
  DataFetcher,
  withFetch
} from "@ui-components/data-fetcher/DataFetcher.tsx";

import { NoObservationDataTile } from "./NoObservationDataTile.tsx";
import { ObservationHeader } from "./ObservationHeader.tsx";
import { ObservationsGroupedView } from "./ObservationsGroupedView.tsx";
import {
  getGroupObservations,
  getGroupsAndItems,
  GroupedObservation,
  GroupedObservationItem,
  groupObservationsByDateTypeAndTimeStamp,
  groupObservationsByExaminationAndDate,
  ObservationGroup
} from "./utils.ts";

interface ObservationsSidePanelProps {
  clinicalRecord: ClinicalRecord;
}

const ObservationsSidePanelBase: React.FC<ObservationsSidePanelProps> =
  observer(({ clinicalRecord }) => {
    const { clinical } = useStores();
    const handleOnShowAsTableClick = () => {
      clinical.ui.setPatientClinicalContent({
        type: ClinicalDataType.Observations
      });
    };

    const [selectedPivotKey, setSelectedPivotKey] = useState<string>(
      ObservationGroup.GroupByDateAndTypeKey
    );

    const handlePivotItemClick = (item?: PivotItem) => {
      if (item?.props.itemKey) {
        setSelectedPivotKey(item.props.itemKey);
      }
    };

    const fetchObservations = async () => {
      const observationsResult = await clinicalRecord.loadObservationData();

      const observations = observationsResult.results;

      const groupedObservationsByDate =
        groupObservationsByDateTypeAndTimeStamp(observations);

      const groupedObservationsByExamination =
        groupObservationsByExaminationAndDate(observations);

      return { groupedObservationsByDate, groupedObservationsByExamination };
    };

    const onRenderCell = (
      nestingDepth?: number,
      item?: GroupedObservationItem
    ): React.ReactNode => {
      if (!item) return null;

      const groupedObservations = getGroupObservations(item, selectedPivotKey);

      return (
        <Stack styles={{ root: { paddingBottom: 4 } }}>
          <ObservationsGroupedView
            pivotKey={selectedPivotKey}
            groupedObservations={groupedObservations}
          />
        </Stack>
      );
    };

    const renderHeader = (
      props: IGroupHeaderProps,
      observations: Record<string, Record<string, GroupedObservation[]>>
    ) => {
      if (props && props.group) {
        return (
          <ObservationHeader
            props={props}
            clinicalRecord={clinicalRecord}
            selectedKey={selectedPivotKey}
            observations={observations}
          />
        );
      }
      return null;
    };

    return (
      <>
        <Stack horizontal>
          <Heading
            variant="section-heading-light"
            styles={{
              root: { paddingRight: 4, paddingTop: 2 }
            }}
          >
            {ClinicalDataTypeLabel.Observations}
          </Heading>
          <TooltipHost content={ObservationGroup.ShowAsTableLabel}>
            <IconButton
              iconProps={{ iconName: "GUID" }}
              onClick={() => handleOnShowAsTableClick()}
              styles={{ icon: { height: "17px" } }}
            />
          </TooltipHost>
        </Stack>
        <Stack styles={{ root: { minHeight: 0 } }}>
          <Pivot
            selectedKey={selectedPivotKey}
            onLinkClick={handlePivotItemClick}
          >
            <PivotItem
              itemKey={ObservationGroup.GroupByDateAndTypeKey}
              headerText={ObservationGroup.GroupByDateAndTypeLabel}
            />
            <PivotItem
              itemKey={ObservationGroup.GroupByExaminationAndDateKey}
              headerText={ObservationGroup.GroupByExaminationAndDateLabel}
            />
          </Pivot>

          <DataFetcher
            fetch={fetchObservations}
            fallback={<Spinner />}
            refetchId={clinicalRecord.clinicalData?.generalExamination?.eTag}
          >
            {result => {
              const groupedObservationsByDate =
                result.groupedObservationsByDate;

              const groupedObservationsByExamination =
                result.groupedObservationsByExamination;

              if (
                (Object.keys(groupedObservationsByDate).length < 1 &&
                  selectedPivotKey ===
                    ObservationGroup.GroupByDateAndTypeKey) ||
                (Object.keys(groupedObservationsByExamination).length < 1 &&
                  selectedPivotKey ===
                    ObservationGroup.GroupByExaminationAndDateKey)
              ) {
                return <NoObservationDataTile />;
              }
              return (
                <GroupedList
                  {...getGroupsAndItems(
                    {
                      groupedByDateAndType: groupedObservationsByDate,
                      groupedByExaminationAndDate:
                        groupedObservationsByExamination
                    },
                    selectedPivotKey,
                    clinical
                  )}
                  onRenderCell={onRenderCell}
                  groupProps={{
                    onRenderHeader: (props: IGroupHeaderProps) => {
                      return renderHeader(
                        props,
                        selectedPivotKey ===
                          ObservationGroup.GroupByDateAndTypeKey
                          ? groupedObservationsByDate
                          : groupedObservationsByExamination
                      );
                    }
                  }}
                  styles={{
                    root: {
                      minHeight: 0,
                      overflow: "auto",
                      overflowX: "hidden"
                    }
                  }}
                />
              );
            }}
          </DataFetcher>
        </Stack>
      </>
    );
  });

export const ObservationsSidePanel = withFetch(
  x => [x.clinical.ref.observationTypes.load()],
  ObservationsSidePanelBase
);
